import { useState, useEffect } from 'react';

import { NavigateNext, NavigateBefore } from '@mui/icons-material';
import { Fab } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import buddhistEraPlugin from 'dayjs/plugin/buddhistEra';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import { useTranslation } from 'next-i18next';
import Calendar, { CalendarTileProperties } from 'react-calendar';

import { CalendarContainer, BackdropStyle, useFabCssClasses } from './style';

import { startOfMonth, endOfMonth, isSameDay } from 'src/helpers/utils';
import { ICPDCourseRound } from 'src/interfaces';

dayjs.extend(isBetweenPlugin);
dayjs.extend(buddhistEraPlugin);

type SelectionRoundCalendarProps = {
  rounds: ICPDCourseRound[];
  selectedRound?: ICPDCourseRound;
  initialDate?: Date;
  onChangeRound: (round: ICPDCourseRound) => void;
};

const SelectionRoundCalendar = ({
  rounds = [],
  initialDate,
  selectedRound,
  onChangeRound,
}: SelectionRoundCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [activeStartDate, setActiveStartDate] = useState<Date | undefined>();
  const [canGoToPreviousMonth, setCanGoToPreviousMonth] = useState(false);
  const [canGoToNextMonth, setCanGoToNextMonth] = useState(false);
  const [firstRoundDate, setFirstRoundDate] = useState<Date | undefined>();
  const [lastRoundDate, setLastRoundDate] = useState<Date | undefined>();
  const [startDateOfCalendar, setStartDateOfCalendar] = useState<Date>(startOfMonth(new Date()));
  const [endDateOfCalendar, setEndDateOfCalendar] = useState<Date>(endOfMonth(new Date()));

  const fabCssClasses = useFabCssClasses();
  const { t } = useTranslation();

  const onChange = (newDate: Date) => {
    setSelectedDate(newDate);
    const round = rounds?.find((r) => isSameDay(r.roundDate, newDate));

    if (round) {
      onChangeRound(round);
    }
  };

  const tileDisabled = ({ date, view }: CalendarTileProperties): boolean => {
    if (view === 'month') {
      const round = rounds?.find((r) => isSameDay(r.roundDate, date));
      return round === undefined;
    }

    return false;
  };

  const tileContent = ({ date, view }: CalendarTileProperties): JSX.Element | null => {
    if (view === 'month') {
      if (selectedRound && isSameDay(selectedRound.expiredDate, date)) {
        return <div className="optional-text">{t('cpd_enrollment.round.calendar.end_training')}</div>;
      }

      if (selectedRound && isSameDay(selectedRound.roundDate, date)) {
        return <div className="optional-text">{t('cpd_enrollment.round.calendar.start_training')}</div>;
      }

      if (rounds?.find((round) => isSameDay(round.roundDate, date))) {
        const selectTrainingRoundKey = 'cpd_enrollment.round.calendar.select_training_round';
        return <div className="optional-text">{t(selectTrainingRoundKey)}</div>;
      }
    }

    return null;
  };

  const tileClassName = ({ date, view }: CalendarTileProperties): string | string[] | null => {
    if (view === 'month') {
      if (selectedRound) {
        if (selectedDate && dayjs(selectedDate).isSame(date, 'day')) {
          return 'selected-date';
        }
        if (dayjs(selectedRound.expiredDate).isSame(date, 'day')) {
          return 'end-date';
        }

        if (dayjs(date).isBetween(selectedRound.roundDate, selectedRound.expiredDate, 'day')) {
          return 'between-training-date';
        }
      }

      if (rounds?.find((round) => dayjs(round.roundDate).isSame(date, 'day'))) {
        return 'selectable-date';
      }
    }

    return null;
  };

  const getCanGoToPreviousMonthFromDateFlag = (date: Date): boolean => {
    if (!date) return false;
    return dayjs(startDateOfCalendar).isBefore(startOfMonth(date));
  };

  const getCanGoToNextMonthFromDateFlag = (date: Date): boolean => {
    if (!date) return false;
    return dayjs(endDateOfCalendar).isAfter(endOfMonth(date));
  };

  const onSelectedActiveStartDate = () => {
    if (selectedRound && dayjs(lastRoundDate).isBefore(selectedRound.expiredDate)) {
      setActiveStartDate(startOfMonth(selectedRound.expiredDate));
    } else if (selectedRound) {
      setActiveStartDate(startOfMonth(selectedRound.expiredDate));
    }
  };

  useEffect(() => {
    const sortedRoundDate = rounds.map((round) => dayjs(round.roundDate).toDate()).sort((a, b) => (a >= b ? 1 : -1));

    if (rounds.length <= 0) {
      return;
    }

    setFirstRoundDate(sortedRoundDate[0]);
    setLastRoundDate(sortedRoundDate[sortedRoundDate.length - 1]);
    setEndDateOfCalendar(endOfMonth(sortedRoundDate[sortedRoundDate.length - 1]));
    setActiveStartDate(startOfMonth(sortedRoundDate[0]));
  }, [rounds]);

  useEffect(() => {
    if (!rounds || rounds.length <= 0) return;

    if (selectedRound) {
      onSelectedActiveStartDate();
      setActiveStartDate(activeStartDate || initialDate || startDateOfCalendar);
    }
  }, [selectedRound]);

  useEffect(() => {
    if (!activeStartDate) return;

    setCanGoToPreviousMonth(
      isSameDay(startDateOfCalendar, activeStartDate) ? false : getCanGoToPreviousMonthFromDateFlag(activeStartDate),
    );
    setCanGoToNextMonth(getCanGoToNextMonthFromDateFlag(activeStartDate));
  }, [activeStartDate]);

  return (
    <CalendarContainer>
      {!rounds ||
        (rounds.length > 0 && !firstRoundDate && !endDateOfCalendar && (
          <BackdropStyle open sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <CircularProgress />
          </BackdropStyle>
        ))}
      <Calendar
        onChange={onChange}
        value={selectedDate}
        locale="th-TH"
        nextLabel={
          <Fab
            size="small"
            classes={{ sizeSmall: fabCssClasses.sizeSmall }}
            className="custom-navigation"
            disableFocusRipple={!canGoToNextMonth}
            disableRipple={!canGoToNextMonth}
            disabled={!canGoToNextMonth}
          >
            <NavigateNext color="primary" sx={{ fontSize: 18 }} />
          </Fab>
        }
        prevLabel={
          <Fab
            size="small"
            classes={{ sizeSmall: fabCssClasses.sizeSmall }}
            className="custom-navigation"
            disableFocusRipple={!canGoToPreviousMonth}
            disableRipple={!canGoToPreviousMonth}
            disabled={!canGoToPreviousMonth}
          >
            <NavigateBefore color="primary" sx={{ fontSize: 18 }} />
          </Fab>
        }
        next2Label={null}
        prev2Label={null}
        tileDisabled={tileDisabled}
        tileContent={tileContent}
        tileClassName={tileClassName}
        calendarType="US"
        minDetail="month"
        minDate={startDateOfCalendar}
        maxDate={endDateOfCalendar}
        defaultActiveStartDate={startDateOfCalendar}
        activeStartDate={activeStartDate}
        onActiveStartDateChange={({ activeStartDate: currentActiveStartDate }) => {
          setActiveStartDate(currentActiveStartDate);
        }}
      />
    </CalendarContainer>
  );
};

export default SelectionRoundCalendar;
